import { SolidStreamingMarkdown } from "@storytell/markdown";
import {
  proseMirrorToRichText,
  RichTextEditor,
  type RichTextEditorInstance,
  richTextToProseMirror,
} from "@storytell/rich-text";
import { clsx } from "clsx";
import { type Component, Show, createMemo, createSignal } from "solid-js";
import { isServer } from "solid-js/web";
import type { ArtifactState } from "~/stores/ArtifactStore";
import { useAppUI, ExtendedPaneMode } from "~/components/layouts/minimal/AppUIContext";
import type { StreamingBlockId } from "@storytell/streaming";
import { dispatchGlobalEvent } from "@storytell/events";
import { TbAlertCircle, TbCopy, TbDeviceFloppy, TbDownload, TbShare3, TbX } from "solid-icons/tb";
import { StButton } from "@storytell/ui/buttons";
import { StTooltip, StTooltipTrigger } from "@storytell/ui/tooltip";
import { type RichText, useExportClient } from "@storytell/cp-client";
import { ExportFormat } from "@storytell/cp-client/src/gen/export/v3/export_service_pb";
import { useWire } from "~/wire";
import { RichTextType, type PmDoc } from "@storytell/types/richText";
import { createMarkdownStreamerFromDeltas } from "./messages/r/internal/markdownStreamerFromDeltas";
import { ArtifactCitationRenderer } from "./ArtifactCitationRenderer";

interface ArtifactViewerProps {
  artifact: ArtifactState;
  isActive: boolean;
  class?: string;
}

export const ArtifactViewer: Component<ArtifactViewerProps> = (props) => {
  const wire = useWire();
  const { actions } = useAppUI();
  // Only render content when active for performance
  const shouldRenderContent = createMemo(() => props.isActive);

  const handleClose = () => {
    actions.setExtendedPaneMode(ExtendedPaneMode.HIDDEN);
  };

  const handlePublish = () => {
    // TODO: Implement publish functionality
    console.log("Publish artifact:", props.artifact.id);
  };

  // Create memo to determine rendering strategy based on processing state and user preference
  const shouldUseRichText = createMemo(() => {
    // Must have rich text data available
    const hasRichText = props.artifact?.metadata?.richText;

    // Must be done processing (stop message received)
    const isDoneProcessing = props.artifact && !props.artifact.isProcessing;

    // User must have rich text replacement enabled (controllable via debug toggle)

    return hasRichText && isDoneProcessing;
  });

  let editorInstance: RichTextEditorInstance | undefined;

  const [download, setDownload] = createSignal({
    downloading: false,
    err: false,
  });

  return (
    <div class={clsx("w-full h-full bg-panel relative", props.isActive ? "block" : "hidden", props.class)}>
      <Show when={shouldRenderContent()}>
        {/* Action buttons positioned at top right */}
        <div class="absolute top-6 right-6 z-10 flex items-center gap-2">
          <Show when={shouldUseRichText()}>
            {/* <StTooltip content={<p>Save to My Knowledge</p>}> */}
            {/*   <StButton as={StTooltipTrigger} icon={TbDeviceFloppy} class="bg-tile hover:bg-tile-hover" /> */}
            {/* </StTooltip> */}
            <StTooltip content={<p>Copy to Clipboard</p>}>
              <StButton
                onClick={() => editorInstance && copyToClipboard(editorInstance)}
                as={StTooltipTrigger}
                icon={TbCopy}
                class="bg-tile hover:bg-tile-hover"
              />
            </StTooltip>
            <StTooltip content={<p>{download().err ? "Click to try again" : "Download"}</p>}>
              <StButton
                loading={download().downloading}
                onClick={async () => {
                  setDownload({ downloading: true, err: false });
                  try {
                    await downloadArtifact(
                      props.artifact,
                      proseMirrorToRichText((editorInstance?.getJSON() ?? {}) as unknown as PmDoc),
                      wire.auth.token,
                    );
                    setDownload({ downloading: false, err: false });
                  } catch (err) {
                    setDownload({ downloading: false, err: true });
                  }
                }}
                as={StTooltipTrigger}
                icon={download().err ? TbAlertCircle : TbDownload}
                class="bg-tile hover:bg-tile-hover"
                classList={{
                  "text-red-500": download().err,
                }}
              >
                {download().err ? "Download failed" : "Download"}
              </StButton>
            </StTooltip>
            {/* <StTooltip content={<p>Share this Artifact</p>}> */}
            {/*   <StButton */}
            {/*     onClick={handlePublish} */}
            {/*     as={StTooltipTrigger} */}
            {/*     icon={TbShare3} */}
            {/*     class="bg-tile hover:bg-tile-hover" */}
            {/*   > */}
            {/*     Share */}
            {/*   </StButton> */}
            {/* </StTooltip> */}
          </Show>

          <StButton class="hover:bg-tile-hover" onClick={handleClose} noColor noBorder icon={TbX} />
        </div>
        {/* Artifact content - full height scrollable area */}
        <div class="h-full overflow-auto">
          <UnifiedArtifactRenderer
            artifact={props.artifact}
            shouldUseRichText={shouldUseRichText}
            onEditorReady={(editor) => {
              editorInstance = editor;
            }}
          />
        </div>
      </Show>
    </div>
  );
};

// Streaming markdown component optimized for artifact content
const StreamingArtifactMarkdown: Component<{ artifact: ArtifactState }> = (props) => {
  const wire = useWire();
  const markdownStreamer = createMarkdownStreamerFromDeltas(
    () => wire.chat.getBlock(props.artifact.blockId as StreamingBlockId)?.deltas?.map((d) => d.text) ?? [],
  );

  return (
    <SolidStreamingMarkdown
      doc={markdownStreamer.doc}
      options={{
        classes: {
          document: "**:animate-fade-in wrap-break-word whitespace-break-spaces px-4",
          heading: (node) => {
            switch (node.level) {
              case 1:
                return "font-medium text-3xl mb-3 mt-4";
              case 2:
                return "font-medium text-2xl mb-3 mt-4";
              case 3:
                return "font-medium text-xl mb-3 mt-4";
              case 4:
                return "font-medium text-lg mb-3 mt-4";
              case 5:
                return "font-medium text-base mb-3 mt-4";
              case 6:
                return "font-medium text-sm mb-3 mt-4";
              default:
                return "";
            }
          },
          // paragraph: "mb-2",
          list: (node) => {
            switch (node.listData?.type) {
              case "ordered":
                return "list-decimal pl-4";
              case "bullet":
                return "list-disc pl-4";
              default:
                return "";
            }
          },
          paragraph: "mb-4",
          // text: "animate-fade-in",
          item: "mb-2",
          code: "bg-black/10 dark:bg-white/10 px-0.5 py-0.5 rounded text-sm",
          blockQuote: "border-l-2 border-indigo-100 dark:border-indigo-700 pl-2 py-1 my-1",
          tableWrapper: "overflow-x-auto w-full max-w-full rounded-lg border border-muted-border",
          table: "",
          tableRow: "border-b border-muted-border [&:last-child]:border-b-0",
          tableCell: "px-2 py-2 border-r border-muted-border [&:last-child]:border-r-0",
          tableHead: "bg-panel text-sm font-semibold [&_td]:whitespace-nowrap",
          tableBody: "bg-tile",
          link: "text-brand underline",
        },
      }}
    />
  );
};

// Unified renderer that handles all artifact types
const UnifiedArtifactRenderer: Component<{
  artifact: ArtifactState;
  shouldUseRichText: () => boolean;
  onEditorReady: (editor: RichTextEditorInstance) => void;
}> = (props) => {
  const { state } = useAppUI();
  let scrollableStartPoint: HTMLDivElement | undefined;

  const [transitionToEditor, setTransitionToEditor] = createSignal(false);

  const shouldUseRichText = createMemo(() => props.shouldUseRichText() && state.useRichTextReplacement);

  const getScrollableParent = () => {
    let current = scrollableStartPoint;
    while (current) {
      if (current.scrollTop > 0) {
        return current;
      }
      current = current.parentElement as HTMLDivElement;
    }
    return scrollableStartPoint;
  };

  return (
    <div class="h-full p-4" ref={scrollableStartPoint}>
      <div class="h-full overflow-auto relative pt-20 pb-20">
        <div class="">
          {/* Left spacer - grows/shrinks more aggressively with panel width */}
          <div class="flex-[2] min-w-2 max-w-100" />

          {/* Content column - maintains readable width */}
          <div class="max-w-[90ch] w-full">
            {/* Always show streaming markdown when rich text is disabled or during transition */}
            <Show when={!shouldUseRichText() || !transitionToEditor()}>
              <StreamingArtifactMarkdown artifact={props.artifact} />
            </Show>

            {/* Only show rich text renderer when enabled and available */}
            <Show when={shouldUseRichText() && props.artifact.metadata?.richText}>
              <div classList={{ "absolute inset-0": !transitionToEditor() }}>
                <RichTextEditor
                  onEditorReady={(editor) => {
                    props.onEditorReady(editor);
                    const parent = getScrollableParent();
                    const scroll = parent?.scrollTop;
                    setTransitionToEditor(true);
                    requestAnimationFrame(() => {
                      parent?.scrollTo({ top: scroll, behavior: "instant" });
                    });
                  }}
                  doc={props.artifact.metadata?.richText!}
                  onQuoteInPromptBar={(data) => {
                    dispatchGlobalEvent("quote-in-prompt-bar", data);
                  }}
                  citationRenderer={ArtifactCitationRenderer}
                />
              </div>
            </Show>
          </div>

          {/* Right spacer - less aggressive scaling to provide padding */}
          <div class="flex-[1] min-w-2 max-w-100" />
        </div>
      </div>
    </div>
  );
};

// Utility functions
const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const copyToClipboard = async (editor: RichTextEditorInstance) => {
  if (isServer) return;
  // 1. Get the content as HTML from the Tiptap editor
  const htmlContent = editor.getHTML();

  // 2. Create a Blob for the HTML content
  //    The 'text/html' MIME type is crucial for rich text copy
  const htmlBlob = new Blob([htmlContent], { type: "text/html" });

  // 3. Create a ClipboardItem.
  //    You can also provide a plain text version if desired for fallback
  const plainTextContent = editor.getText();
  const plainTextBlob = new Blob([plainTextContent], { type: "text/plain" });

  const clipboardItem = new ClipboardItem({
    "text/html": htmlBlob,
    "text/plain": plainTextBlob,
  });

  try {
    // 4. Write the ClipboardItem to the clipboard
    await navigator.clipboard.write([clipboardItem]);
    console.log("Editor content (rich text) copied to clipboard successfully!");
  } catch (err) {
    console.error("Failed to copy editor content:", err);
    // Fallback for older browsers or if Clipboard API fails for some reason
    // You might try document.execCommand('copy') here, but it's less reliable for rich text
    // and generally requires a selection or focus.
    alert("Failed to copy content. Please try copying manually or check console for details.");
  }
};

const findTitle = (richText: RichText.RichText) => {
  return richText.content
    .find((node) => node.type === RichTextType.HeadingType)
    ?.content.find((node) => node.type === RichTextType.TextType)?.text;
};

const downloadArtifact = async (
  artifact: ArtifactState,
  richText: RichText.RichText,
  authToken: () => string | undefined,
) => {
  if (isServer) return;
  const title = findTitle(richText) || artifact.title;

  const result = await useExportClient({ authToken }).exportRichText({
    richText: richText,
    filename: title,
    format: ExportFormat.PDF,
  });

  if (result.isErr()) {
    console.error("Failed to download artifact:", result.error);
    throw result.error;
  }

  try {
    const blob = new Blob([result.value.fileData], { type: result.value.contentType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${title.replace(/[^a-z0-9\s'":-]/gi, "_")}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error("Failed to download artifact:", err);
  }
};
